import { PaletteMode, Components, Theme } from '@mui/material';
import { themePalette } from '../theme.palette';
import { typographyTokens } from '../typography-tokens';
import dimensions from '../dimensions';

export const MuiTabsTheme = (mode: PaletteMode): Components<Theme>['MuiTabs'] => {
  const { componentTokens } = themePalette(mode);
  const { spacing } = dimensions;

  return {
    styleOverrides: {
      root: {
        '& .MuiTabs-list': {
          gap: spacing(2), // 8px
        },
        minHeight: 'auto',
        '& .MuiTabs-scrollButtons': {
          display: 'flex',
        },
        // Underline variant
        '&[data-variant="underline"]': {
          '& .MuiTabs-indicator': {
            display: 'block',
            backgroundColor: componentTokens.tabs.colors.underline.stroke.selected,
            height: 2,
            borderRadius: spacing(1.5),
            zIndex: 1,
          },
        },
        // Contained variant (default)
        '&[data-variant="contained"]': {
          '& .MuiTabs-indicator': {
            display: 'none',
          },
        },
        // Outline variant
        '&[data-variant="outline"]': {
          '& .MuiTabs-indicator': {
            display: 'none',
          },
        },
        // Text variant
        '&[data-variant="text"]': {
          '& .MuiTabs-indicator': {
            display: 'none',
          },
        },
      },
      indicator: {
        display: 'none', // Hidden by default, shown for underline variant
      },
    },
  };
};

export const MuiTabTheme = (mode: PaletteMode): Components<Theme>['MuiTab'] => {
  const { componentTokens } = themePalette(mode);
  const { spacing } = dimensions;

  return {
    styleOverrides: {
      root: {
        minHeight: 'auto',
        padding: '8px 12px',
        marginBottom: spacing(1.5), // 6px
        borderRadius: spacing(1.5), // 6px
        color: componentTokens.tabs.colors.contained.text.resting,
        letterSpacing: 'normal',
        textTransform: 'none',
        transition: 'color 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms',
        '&.MuiButtonBase-root': {
          fontSize: typographyTokens.fontSizes.base,
          lineHeight: typographyTokens.lineHeights.base,
          fontWeight: typographyTokens.fontWeights.medium,
        },
        // Contained variant styling
        '[data-variant="contained"] &': {
          backgroundColor: componentTokens.tabs.colors.contained.background.resting,
          color: componentTokens.tabs.colors.contained.text.resting,
          '& .MuiSvgIcon-root': {
            color: componentTokens.tabs.colors.contained.assets.resting,
          },
          '&.Mui-selected': {
            color: componentTokens.tabs.colors.contained.text.selected,
            backgroundColor: componentTokens.tabs.colors.contained.background.selected,
            borderColor: componentTokens.tabs.colors.contained.stroke.selected,
            '& .MuiSvgIcon-root': {
              color: componentTokens.tabs.colors.contained.assets.selected,
            },
          },
          '&:hover': {
            color: componentTokens.tabs.colors.contained.text.hover,
            backgroundColor: componentTokens.tabs.colors.contained.background.hover,
            '& .MuiSvgIcon-root': {
              color: componentTokens.tabs.colors.contained.assets.hover,
            },
          },
          '&:focus, &.Mui-focusVisible': {
            color: componentTokens.tabs.colors.contained.text.selected,
            backgroundColor: componentTokens.tabs.colors.contained.background.selected,
            outline: 'none',
            '& .MuiSvgIcon-root': {
              color: componentTokens.tabs.colors.contained.assets.selected,
            },
          },
          '&.Mui-selected:hover, &.Mui-selected:focus, &.Mui-selected.Mui-focusVisible': {
            backgroundColor: componentTokens.tabs.colors.contained.background.selectedHover,
            color: componentTokens.tabs.colors.contained.text.selectedHover,
            outline: 'none',
            '& .MuiSvgIcon-root': {
              color: componentTokens.tabs.colors.contained.assets.selectedHover,
            },
          },
        },

        // Underline variant styling
        '[data-variant="underline"] &': {
          color: componentTokens.tabs.colors.underline.text.resting,
          backgroundColor: componentTokens.tabs.colors.underline.background.resting,
          '& .MuiSvgIcon-root': {
            color: componentTokens.tabs.colors.underline.assets.resting,
          },
          '&.Mui-selected': {
            color: componentTokens.tabs.colors.underline.text.selected,
            backgroundColor: componentTokens.tabs.colors.underline.background.selected,
            borderColor: componentTokens.tabs.colors.underline.stroke.selected,
            '& .MuiSvgIcon-root': {
              color: componentTokens.tabs.colors.underline.assets.selected,
            },
          },
          '&:hover': {
            color: componentTokens.tabs.colors.underline.text.hover,
            backgroundColor: componentTokens.tabs.colors.underline.background.hover,
            '& .MuiSvgIcon-root': {
              color: componentTokens.tabs.colors.underline.assets.hover,
            },
          },
          '&:focus, &.Mui-focusVisible': {
            color: componentTokens.tabs.colors.underline.text.hover,
            backgroundColor: componentTokens.tabs.colors.underline.background.hover,
            '& .MuiSvgIcon-root': {
              color: componentTokens.tabs.colors.underline.assets.hover,
            },
          },
          '&.Mui-selected:hover, &.Mui-selected:focus, &.Mui-selected.Mui-focusVisible': {
            backgroundColor: componentTokens.tabs.colors.underline.background.hover,
            color: componentTokens.tabs.colors.underline.text.hover,
            outline: 'none',
            '& .MuiSvgIcon-root': {
              color: componentTokens.tabs.colors.underline.assets.selectedHover,
            },
          },
        },

        // Outline variant styling
        '[data-variant="outline"] &': {
          border: '1px solid',
          borderColor: componentTokens.tabs.colors.outline.stroke.resting,
          backgroundColor: 'transparent',
          color: componentTokens.tabs.colors.outline.text.resting,
          marginRight: spacing(1), // 4px spacing between tabs
          '& .MuiSvgIcon-root': {
            color: componentTokens.tabs.colors.outline.assets.resting,
          },
          '&.Mui-selected': {
            color: componentTokens.tabs.colors.outline.text.selected,
            backgroundColor: componentTokens.tabs.colors.outline.background.selected,
            borderColor: componentTokens.tabs.colors.outline.stroke.selected,
            '& .MuiSvgIcon-root': {
              color: componentTokens.tabs.colors.outline.assets.selected,
            },
          },
          '&:hover': {
            color: componentTokens.tabs.colors.outline.text.hover,
            backgroundColor: componentTokens.tabs.colors.outline.background.hover,
            borderColor: componentTokens.tabs.colors.outline.stroke.hover,
            '& .MuiSvgIcon-root': {
              color: componentTokens.tabs.colors.outline.assets.hover,
            },
          },
          '&:focus, &.Mui-focusVisible': {
            color: componentTokens.tabs.colors.outline.text.hover,
            backgroundColor: componentTokens.tabs.colors.outline.background.hover,
            borderColor: componentTokens.tabs.colors.outline.stroke.hover,
            outline: 'none',
            '& .MuiSvgIcon-root': {
              color: componentTokens.tabs.colors.outline.assets.hover,
            },
          },
          '&.Mui-selected:hover, &.Mui-selected:focus, &.Mui-selected.Mui-focusVisible': {
            backgroundColor: componentTokens.tabs.colors.outline.background.selectedHover,
            color: componentTokens.tabs.colors.outline.text.selectedHover,
            borderColor: componentTokens.tabs.colors.outline.stroke.selectedHover,
            outline: 'none',
            '& .MuiSvgIcon-root': {
              color: componentTokens.tabs.colors.outline.assets.selectedHover,
            },
          },
        },

        // Text variant styling
        '[data-variant="text"] &': {
          border: 'none',
          borderRadius: 0,
          backgroundColor: 'transparent',
          color: componentTokens.tabs.colors.textOnly.text.resting,
          marginRight: spacing(3), // 12px spacing between tabs
          padding: '12px 8px',
          '& .MuiSvgIcon-root': {
            color: componentTokens.tabs.colors.textOnly.assets.resting,
          },
          '&.Mui-selected': {
            color: componentTokens.tabs.colors.textOnly.text.selected,
            backgroundColor: 'transparent',
            '& .MuiSvgIcon-root': {
              color: componentTokens.tabs.colors.textOnly.assets.selected,
            },
          },
          '&:hover': {
            color: componentTokens.tabs.colors.textOnly.text.hover,
            backgroundColor: 'transparent',
            '& .MuiSvgIcon-root': {
              color: componentTokens.tabs.colors.textOnly.assets.hover,
            },
          },
          '&:focus, &.Mui-focusVisible': {
            color: componentTokens.tabs.colors.textOnly.text.hover,
            backgroundColor: 'transparent',
            outline: 'none',
            '& .MuiSvgIcon-root': {
              color: componentTokens.tabs.colors.textOnly.assets.hover,
            },
          },
          '&.Mui-selected:hover, &.Mui-selected:focus, &.Mui-selected.Mui-focusVisible': {
            color: componentTokens.tabs.colors.textOnly.text.selectedHover,
            backgroundColor: 'transparent',
            outline: 'none',
            '& .MuiSvgIcon-root': {
              color: componentTokens.tabs.colors.textOnly.assets.selectedHover,
            },
          },
        },

        // Small size: reduced padding, smaller text, and min-width 0 (applies to all variants)
        '[data-size="small"] &': {
          minWidth: 0,
          padding: '8px',
          '&.MuiButtonBase-root': {
            fontSize: typographyTokens.fontSizes.xs, // 12px
            lineHeight: typographyTokens.lineHeights.xxs, // 12px
            fontWeight: typographyTokens.fontWeights.semibold, // 600
          },
        },
      },
    },
  };
};
